import { Product } from 'component-library/__product'
import { themes } from 'component-library/__styling/themes'
import { themedValue } from 'component-library/__styling/themed-value'
import { breakpoints } from 'component-library/__styling/settings'
import { ImageWidth } from 'common/helpers/image'
import { get2xNearestImageWidth } from './imageManager'

export const enum ImageSizeTypes {
    Article,
    ListingCard,
    Card,
    LargeCard,
    ProfileHeadshot,
    GalleryHeader,
    GalleryItem,
    GalleryThumbnail,
    BreakingNews,
    FeatureHero,
}

/**
 * Determines the sizes for different image types,
 * we try to return a best fit all result for the component size type, doing per product allows better sizes
 * as each product varys with sidebar's etc
 */
export const getImageSizePropsFromType = (
    product: Product,
    imageSizeType: ImageSizeTypes,
    imageWidth: ImageWidth
) => {
    const filtered = themes.filter(t => t.kind === product)

    if (filtered.length === 0) {
        throw new Error('Unable to get theme from product')
    }
    const theme = filtered[0]

    /**
     * Returns a sizes string that corresponds to what the view-port width across mobile, tablet should be
     * for desktop the nearest imagewidth midpoint is used if available otherwise the intial imagewidth
     */
    const vwVals = (vwMobile: number, vwTablet: number) => {
        const mobileMax = breakpoints.sm - 1
        const tabletMax = breakpoints.md

        const nearest2xImage = get2xNearestImageWidth(product, imageWidth)
        // this effectively rounds the image width upwards as srcset rounds either way
        // sizes will multiply this by the DPR, so it needs to be 1x
        const nearest1xImageWidth = nearest2xImage ? Math.ceil(nearest2xImage / 2) : imageWidth

        // Doing this might be a little redundant now that we have source elements that trigger different ratio's
        // for different breakpoints, just means CSS has to do slightly more, but this can stay for the meantime
        // might be a perf issue later down the track when more images are swapping ratio's at diff breakpoints
        return `(max-width: ${mobileMax}px) ${vwMobile}vw, (min-width: ${mobileMax +
            1}px) and (max-width: ${tabletMax}px) ${vwTablet}vw, ${nearest1xImageWidth}px`
    }

    switch (imageSizeType) {
        case ImageSizeTypes.Article:
            return themedValue(theme, {
                thewest: vwVals(90, 90),
                perthnow: vwVals(100, 100),
                sevennews: vwVals(95, 50),
            })
        case ImageSizeTypes.Card:
            return themedValue(theme, {
                thewest: vwVals(40, 25),
                perthnow: vwVals(40, 17),
                sevennews: vwVals(17, 17),
            })
        case ImageSizeTypes.ListingCard:
            return themedValue(theme, {
                fallback: vwVals(100, 50),
                sevennews: vwVals(100, 50),
            })
        case ImageSizeTypes.LargeCard:
            return themedValue(theme, {
                thewest: vwVals(100, 50),
                perthnow: vwVals(100, 100),
                sevennews: vwVals(100, 50),
            })
        case ImageSizeTypes.ProfileHeadshot:
            return themedValue(theme, { fallback: vwVals(50, 33) })
        case ImageSizeTypes.GalleryHeader:
            return themedValue(theme, {
                fallback: vwVals(100, 100),
            })
        case ImageSizeTypes.GalleryItem:
            return themedValue(theme, {
                fallback: vwVals(100, 100),
            })
        case ImageSizeTypes.GalleryThumbnail:
            return themedValue(theme, {
                perthnow: vwVals(14, 16),
                thewest: vwVals(40, 20),
                fallback: vwVals(100, 50),
            })
        case ImageSizeTypes.BreakingNews:
            return themedValue(theme, { fallback: vwVals(50, 33) })
        case ImageSizeTypes.FeatureHero:
            return themedValue(theme, {
                fallback: vwVals(90, 90),
            })
        default:
            return themedValue(theme, { fallback: '100vw' })
    }
}
